import {
  APARTMENT_BATCH_MAX_ROWS,
  type ApartmentBatchRequest,
  type ApartmentBatchResponse,
  LEGACY_LAYOUTS,
  type LegacyLayout,
  apartmentBatchResponseSchema,
  type RestrictedAccess,
  isPersonalField,
  mayAccessField,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CellFocusRequest } from './SheetGrid';
import type { SortColumn } from 'react-data-grid';
import { apartmentHref } from '../apartment/location';
import { AppLink } from '../ui/AppLink';
import { ApartmentCards } from './ApartmentCards';
import { ApartmentForm, type FormChange } from './ApartmentForm';
import { ApartmentGrid, apartmentSheetColumns } from './ApartmentGrid';
import { ApartmentReview } from './ApartmentReview';
import { UndoButtons } from './UndoButtons';
import { Modal } from './Modal';
import {
  type SheetRow,
  type Structures,
  applyPaste,
  applyResult,
  clearCell,
  discardRow,
  displayValue,
  keepMyChange,
  kindOf,
  newRow,
  rowState,
  rowsFromSheet,
  setCell,
  summarize,
  toBatchRows,
  usedSlots,
} from './apartment-draft';
import { type PastePermissions, checkLayout, checkTargets, confirmLegacy } from './apartment-paste';
import type { ClipboardTable } from './clipboard';
import { and, count } from './labels';
import type { ApartmentSheetData } from './use-master-data';
import { useHistory } from './use-history';
import { useBatchReview } from './use-batch-review';
import { useNarrowScreen } from './use-narrow-screen';
import { reportPendingChanges, warnOnUnload } from './unsaved-changes';

interface ApartmentSheetProps {
  readonly projectId: string;
  readonly data: ApartmentSheetData;
  readonly permissions: PastePermissions;
  readonly onSaved: (message: string) => void;
  readonly savedMessage: string | null;
}

interface Filters {
  readonly search: string;
  readonly building: string;
  readonly pendingOnly: boolean;
}

function compare(a: string, b: string): number {
  const numeric = /^-?\d+(\.\d+)?$/;
  if (numeric.test(a) && numeric.test(b)) return Number(a) - Number(b);
  return a.localeCompare(b, 'he', { numeric: true });
}

/** Stored rows matching the filters, every pending row, then the placeholder; optionally sorted. */
function displayedRows(rows: readonly SheetRow[], filters: Filters, sort: readonly SortColumn[]) {
  const search = filters.search.trim().toLowerCase();
  const matches = (row: SheetRow) =>
    rowState(row) !== 'stored' ||
    (!filters.pendingOnly &&
      (search === '' || displayValue(row, 'apartmentNumber').toLowerCase().includes(search)) &&
      (filters.building === '' || displayValue(row, 'building') === filters.building));
  const body = rows.filter((row) => rowState(row) !== 'placeholder' && matches(row));
  const placeholder = rows.filter((row) => rowState(row) === 'placeholder');
  const [first] = sort;
  if (first !== undefined) {
    const field = first.columnKey;
    const direction = first.direction === 'DESC' ? -1 : 1;
    body.sort((a, b) => direction * compare(displayValue(a, field), displayValue(b, field)));
  }
  return [...body, ...placeholder];
}

interface PendingLegacy {
  readonly table: string[][];
  readonly anchorIndex: number;
  readonly headerDropped: boolean;
  readonly layout: LegacyLayout;
  /** The range also fits the current columns as it is: the user chooses. */
  readonly alsoFits: boolean;
}

const PAIR_NAMES = { parking: 'חניה', storage: 'מחסן' } as const;

/**
 * The Apartments sheet of one project (docs/16 §4–6, MD-1): the Tayasim layout, a draft in memory
 * only, Excel paste with layout and privacy guards, and one unified review before an atomic save.
 */
export function ApartmentSheet({
  projectId,
  data,
  permissions: base,
  onSaved,
  savedMessage,
}: ApartmentSheetProps) {
  const structures: Structures = data.structures;
  // Contacts (DR-185) and the note (DR-187), each with its own keys: shown when the server sent
  // them, editable with the edit key.
  const restricted: RestrictedAccess = useMemo(
    () => ({ contact: data.personal.view, note: data.note.view }),
    [data.personal.view, data.note.view],
  );
  const permissions: PastePermissions = useMemo(
    () => ({ ...base, contact: data.personal.edit, note: data.note.edit }),
    [base, data.personal.edit, data.note.edit],
  );
  const narrow = useNarrowScreen();
  // The draft, with undo / redo of unsaved edits (memory only).
  const history = useHistory<SheetRow[]>(() => rowsFromSheet(data.apartments));
  const rows = history.value;
  const setRows = history.change;
  const [filters, setFilters] = useState<Filters>({ search: '', building: '', pendingOnly: false });
  const [sort, setSort] = useState<readonly SortColumn[]>([]);
  const [message, setMessage] = useState<{
    text: string;
    tone: 'info' | 'success' | 'error';
  } | null>(savedMessage === null ? null : { tone: 'success', text: savedMessage });
  const [legacy, setLegacy] = useState<PendingLegacy | null>(null);
  // Additional parking / storage column pairs the user added; the sheet always shows at least as
  // many as any row uses, so no allocation is ever hidden.
  const [added, setAdded] = useState({ parking: 0, storage: 0 });
  const needed = useMemo(
    () => ({
      parking: Math.max(0, ...rows.map((row) => usedSlots(row, 'parking') - 1)),
      storage: Math.max(0, ...rows.map((row) => usedSlots(row, 'storage') - 1)),
    }),
    [rows],
  );
  const extra = {
    parking: Math.max(added.parking, needed.parking),
    storage: Math.max(added.storage, needed.storage),
  };
  const columns = useMemo(
    () => apartmentSheetColumns({ parking: extra.parking, storage: extra.storage }, restricted),
    [extra.parking, extra.storage, restricted],
  );
  const addPair = (kind: 'parking' | 'storage') => {
    setAdded((current) => ({ ...current, [kind]: extra[kind] + 1 }));
    setMessage({
      tone: 'info',
      text: `נוספו עמודות ${PAIR_NAMES[kind]} נוספת ${String(extra[kind] + 1)} בסוף הגיליון. הן ריקות עד שתזינו או תדביקו בהן.`,
    });
  };
  const removePair = (kind: 'parking' | 'storage') => {
    setAdded((current) => ({ ...current, [kind]: extra[kind] - 1 }));
  };
  const [editing, setEditing] = useState<SheetRow | null>(null);
  // The row of the active cell: its apartment workspace is one link away (saved rows only).
  const [activeRow, setActiveRow] = useState<SheetRow | null>(null);
  const [focus, setFocus] = useState<CellFocusRequest | null>(null);

  const displayed = useMemo(() => displayedRows(rows, filters, sort), [rows, filters, sort]);
  const pending = summarize(rows);
  const pendingTotal = pending.changed + pending.created;
  const hasPending = pendingTotal > 0;
  const ordered =
    sort.length > 0 || filters.search !== '' || filters.building !== '' || filters.pendingOnly;
  const buildings = structures.filter((s) => s.kind !== 'parking' && s.kind !== 'storage');

  useEffect(() => {
    reportPendingChanges(pendingTotal);
  }, [pendingTotal]);
  useEffect(() => {
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, []);

  const canEdit = useCallback(
    (row: SheetRow, field: string) =>
      isPersonalField(field)
        ? mayAccessField(field, permissions)
        : kindOf(field) !== undefined
          ? permissions.allocate
          : row.unitId === null
            ? permissions.create
            : permissions.edit,
    [permissions],
  );

  const place = (
    table: string[][],
    anchorIndex: number,
    anchorColumn: number,
    headerDropped: boolean,
    dropped: readonly string[],
  ) => {
    const problem = checkTargets(table, displayed, anchorIndex, anchorColumn, columns, permissions);
    if (problem !== null) {
      setMessage({ tone: 'error', text: problem });
      return;
    }
    const result = applyPaste(
      rows,
      displayed,
      table,
      anchorIndex,
      anchorColumn,
      structures,
      columns.map((c) => c.key),
    );
    setRows(() => result.rows);
    const notes = [
      headerDropped ? 'שורת הכותרות לא הודבקה.' : '',
      dropped.length > 0
        ? `העמודות ${dropped.map((d) => `"${d}"`).join(' ו-')} הושמטו לפי אישורך.`
        : '',
      'תאים ריקים לא שינו ערכים קיימים. דבר לא נשמר עדיין.',
      ordered ? 'ההדבקה חלה על השורות כפי שהן מוצגות כעת (לפי הסינון והמיון).' : '',
    ].filter((n) => n !== '');
    setMessage({
      tone: 'info',
      text: `הודבקו ${count(table.length, 'שורה אחת', 'שורות')}: ${changes(result.updatedRows, result.newRows)}. ${notes.join(' ')}`,
    });
  };

  const onPaste = (table: ClipboardTable, anchorIndex: number, anchorColumn: number) => {
    const layout = checkLayout(table, anchorColumn, columns.length);
    if (layout.kind === 'rejected') {
      setMessage({ tone: 'error', text: layout.message });
      return;
    }
    if (layout.table.length === 0) {
      setMessage({ tone: 'info', text: 'הודבקה שורת כותרות בלבד. דבר לא שונה.' });
      return;
    }
    if (layout.kind === 'legacy') {
      setLegacy({
        table: layout.table,
        anchorIndex,
        headerDropped: layout.headerDropped,
        layout: layout.layout,
        alsoFits: layout.alsoFits,
      });
      return;
    }
    place(layout.table, anchorIndex, anchorColumn, layout.headerDropped, []);
  };

  const edit = (key: string, field: string, value: string, clear = false) => {
    setRows((current) => {
      const { rows: next, refused } = clear
        ? clearCell(current, key, field, structures)
        : setCell(current, key, field, value, structures);
      if (refused === 'not_clearable')
        setMessage({ tone: 'error', text: 'לא ניתן לרוקן את הבניין או את מספר הדירה.' });
      return next;
    });
  };

  const applyForm = (key: string, changes: readonly FormChange[]) => {
    let next = rows.some((row) => row.key === key)
      ? rows
      : [...rows.filter((r) => rowState(r) !== 'placeholder'), { ...newRow(), key }];
    for (const change of changes)
      next = setCell(next, key, change.field, change.value, structures).rows;
    setRows(() => next);
    setEditing(null);
  };

  const flow = useBatchReview<ApartmentBatchRequest, ApartmentBatchResponse>({
    previewPath: `/projects/${projectId}/apartment-sheet/batch/preview`,
    commitPath: `/projects/${projectId}/apartment-sheet/batch`,
    schema: apartmentBatchResponseSchema,
    annotate: (result) => {
      history.annotate((current) => applyResult(current, result));
    },
    onSaved: (result) => {
      const s = result.summary;
      const spaces = s.spaces > 0 ? `, ${count(s.spaces, 'מקום חדש אחד', 'מקומות חדשים')}` : '';
      onSaved(
        `השינויים נשמרו: ${and(count(s.create, 'דירה חדשה אחת', 'דירות חדשות'), count(s.update, 'עדכון אחד', 'עדכונים'))}, ${and(count(s.assign, 'הצמדה חדשה אחת', 'הצמדות חדשות'), count(s.end, 'הצמדה אחת הסתיימה', 'הצמדות הסתיימו'))}${spaces}.`,
      );
    },
    onError: (text) => {
      setMessage({ tone: 'error', text });
    },
  });
  const review = flow.review;

  const startReview = () => {
    const batchRows = toBatchRows(rows);
    if (batchRows.length > APARTMENT_BATCH_MAX_ROWS) {
      setMessage({
        tone: 'error',
        text: `ניתן לשמור עד ${String(APARTMENT_BATCH_MAX_ROWS)} שורות בפעם אחת (ממתינות ${String(batchRows.length)}). שמרו בחלקים.`,
      });
      return;
    }
    flow.start({ batchId: crypto.randomUUID(), rows: batchRows, confirmedEnds: [] });
  };

  const discardAll = async () => {
    const question =
      pendingTotal === 1
        ? 'לבטל את השינוי הממתין?'
        : `לבטל את כל ${String(pendingTotal)} השינויים הממתינים?`;
    if (!(await askConfirm({ title: question, confirmLabel: 'ביטול השינויים', danger: true })))
      return;
    history.reset(rowsFromSheet(data.apartments));
    setMessage({ tone: 'info', text: 'השינויים הממתינים בוטלו.' });
  };

  const anyEdit = permissions.create || permissions.edit || permissions.allocate;

  return (
    <section className="workspace" aria-labelledby="sheet-title">
      <div className="toolbar">
        <h2 id="sheet-title" className="toolbar__title">
          דירות
        </h2>
        <label className="toolbar__field">
          <span>חיפוש</span>
          <input
            type="search"
            value={filters.search}
            placeholder="מספר דירה"
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
            }}
          />
        </label>
        <label className="toolbar__field">
          <span>בנין</span>
          <select
            value={filters.building}
            onChange={(e) => {
              setFilters({ ...filters, building: e.target.value });
            }}
          >
            <option value="">הכול</option>
            {buildings.map((s) => (
              <option key={s.id} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="toolbar__toggle">
          <input
            type="checkbox"
            checked={filters.pendingOnly}
            onChange={(e) => {
              setFilters({ ...filters, pendingOnly: e.target.checked });
            }}
          />
          <span>שינויים ממתינים בלבד</span>
        </label>
        <div className="toolbar__actions">
          <p className="pending-count" aria-live="polite">
            {hasPending
              ? `ממתינים לשמירה: ${count(pending.changed, 'עדכון אחד', 'עדכונים')}, ${count(pending.created, 'דירה חדשה אחת', 'חדשות')}${pending.withErrors > 0 ? `, ${count(pending.withErrors, 'אחת עם שגיאה', 'עם שגיאות')}` : ''}`
              : 'אין שינויים ממתינים'}
          </p>
          {narrow && permissions.create && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setEditing(newRow());
              }}
            >
              הוספת דירה
            </button>
          )}
          <UndoButtons
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            onUndo={history.undo}
            onRedo={history.redo}
          />
          <button
            type="button"
            className="button"
            disabled={!hasPending}
            onClick={() => void discardAll()}
          >
            ביטול שינויים
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={!hasPending}
            onClick={startReview}
          >
            בדיקה ושמירה
          </button>
        </div>
      </div>

      <div
        role="status"
        aria-live="polite"
        className={message === null ? 'visually-hidden' : `notice notice--${message.tone}`}
      >
        {message?.text}
      </div>
      {data.truncated && (
        <p className="notice notice--warning">
          מוצגות {data.apartments.length} הדירות הראשונות בלבד.
        </p>
      )}

      {narrow ? (
        <ApartmentCards
          projectId={projectId}
          rows={displayed}
          canEdit={anyEdit}
          restricted={restricted}
          onEdit={setEditing}
        />
      ) : (
        <>
          <details className="paste-help">
            <summary>הדבקה מ-Excel: מה נתמך בשלב זה</summary>
            <p>
              העתיקו טווח מגיליון הדירות והדביקו (Ctrl/Cmd+V) החל מהתא הנבחר. כל שורה היא דירה אחת,
              והחניה והמחסן שבשורה מוצמדים לאותה דירה. שורות נוספות נוצרות אוטומטית. תא ריק בהדבקה
              אינו מוחק ערך קיים; למחיקה מפורשת בחרו תא ולחצו Delete. דבר לא נשמר לפני בדיקה ואישור.
            </p>
            <p>
              לדירה יכולות להיות כמה חניות וכמה מחסנים: "+ הוסף חניה" ו-"+ הוסף מחסן" מוסיפים זוג
              עמודות בסוף הגיליון, וכל דירה ממלאת רק את מה שהיא צריכה. כל זוג הוא הצמדה נפרדת.
            </p>
            <p>
              עמודות פרטי הדיירים ועמודת ההערות מוצגות רק למי שמורשה לכל אחת מהן בנפרד; הדבקה שכוללת
              ערכים בעמודה שאינה זמינה לכם נדחית. גיליון במבנה ישן (17 עמודות, או 16 עם "תעודת
              זהות") מזוהה, ועמודותיו העודפות מושמטות רק לאחר אישורכם.
            </p>
            <p>
              בחירת טווח: Shift+לחיצה או Shift+חצים; לחיצה על מספר שורה בוחרת את כל השורה.
              Ctrl/Cmd+C מעתיק את הטווח לאקסל, Delete מנקה אותו במפורש, Ctrl/Cmd+Z מבטל
              ו-Ctrl/Cmd+Shift+Z מבצע מחדש. לא נתמכים כרגע: גרירת מילוי והקפאת עמודות לבחירה.
            </p>
          </details>
          <div className="pair-actions" role="group" aria-label="עמודות חניה ומחסן נוספות">
            {(['parking', 'storage'] as const).map((kind) => (
              <span key={kind} className="pair-actions__kind">
                <button
                  type="button"
                  className="button button--small"
                  disabled={!permissions.allocate}
                  onClick={() => {
                    addPair(kind);
                  }}
                >
                  + הוסף {PAIR_NAMES[kind]}
                </button>
                {extra[kind] > needed[kind] && (
                  <button
                    type="button"
                    className="button button--small button--ghost"
                    onClick={() => {
                      removePair(kind);
                    }}
                    title="מסיר רק עמודות ריקות שעדיין לא נעשה בהן שימוש"
                  >
                    הסרת {PAIR_NAMES[kind]} נוספת {String(extra[kind])}
                  </button>
                )}
              </span>
            ))}
          </div>
          <p className="sheet-row-link" aria-live="polite">
            {activeRow?.unitId != null ? (
              <AppLink to={apartmentHref(projectId, activeRow.unitId)}>
                לסביבת הדירה: בניין {displayValue(activeRow, 'building')} · דירה{' '}
                {displayValue(activeRow, 'apartmentNumber')}
              </AppLink>
            ) : (
              <span className="hint">בחרו תא בשורה של דירה שמורה כדי לפתוח את סביבת הדירה.</span>
            )}
          </p>
          <ApartmentGrid
            onActiveRowChange={setActiveRow}
            rows={displayed}
            columns={columns}
            focus={focus}
            canEdit={canEdit}
            restricted={restricted}
            sortColumns={sort}
            onSortColumnsChange={setSort}
            onCellChange={(key, field, value) => {
              edit(key, field, value);
            }}
            onCellClear={(key, field) => {
              edit(key, field, '', true);
            }}
            onPaste={onPaste}
            onMessage={(text, tone) => {
              setMessage({ tone, text });
            }}
            onUndo={history.undo}
            onRedo={history.redo}
          />
        </>
      )}

      <Modal
        title="זוהה מבנה גיליון ישן"
        open={legacy !== null}
        onClose={() => {
          setLegacy(null);
        }}
      >
        {legacy !== null && (
          <p>
            הטווח שהודבק כולל {LEGACY_LAYOUTS[legacy.layout].width} עמודות, כמו ב
            {legacy.layout === 'source' ? 'גיליון המקורי' : 'מבנה הקודם של גיליון הדירות'}. העמודות{' '}
            {LEGACY_LAYOUTS[legacy.layout].dropped.map((d) => `"${d}"`).join(' ו-')} אינן חלק
            מגיליון הדירות. כדי שהעמודות לא יוזזו, הן יושמטו לפני ההדבקה וערכיהן לא יישמרו.
            {legacy.alsoFits &&
              ' הטווח מתאים גם לעמודות הנוכחיות של הגיליון (כולל עמודות המקור והעמודות הנוספות); אם זו הכוונה, בחרו "הדבקה לעמודות הנוכחיות".'}
          </p>
        )}
        <footer className="modal__footer">
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              if (legacy !== null)
                place(
                  confirmLegacy(legacy.table, legacy.layout),
                  legacy.anchorIndex,
                  0,
                  legacy.headerDropped,
                  LEGACY_LAYOUTS[legacy.layout].dropped,
                );
              setLegacy(null);
            }}
          >
            השמטת העמודות והדבקה
          </button>
          {legacy?.alsoFits === true && (
            <button
              type="button"
              className="button"
              onClick={() => {
                place(legacy.table, legacy.anchorIndex, 0, legacy.headerDropped, []);
                setLegacy(null);
              }}
            >
              הדבקה לעמודות הנוכחיות
            </button>
          )}
          <button
            type="button"
            className="button"
            onClick={() => {
              setLegacy(null);
              setMessage({ tone: 'info', text: 'ההדבקה בוטלה. דבר לא שונה.' });
            }}
          >
            ביטול ההדבקה
          </button>
        </footer>
      </Modal>

      {editing !== null && (
        <ApartmentForm
          key={editing.key}
          row={editing}
          structures={structures}
          permissions={permissions}
          restricted={restricted}
          onApply={applyForm}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}

      <ApartmentReview
        open={review !== null}
        phase={review?.phase ?? 'reviewing'}
        result={review?.result ?? null}
        rows={rows}
        confirmed={review?.confirmed ?? new Set()}
        onToggleConfirm={flow.toggle}
        onConfirm={flow.commit}
        onClose={flow.close}
        onKeepMine={(key) => {
          setRows((current) => keepMyChange(current, key));
          flow.close();
        }}
        onDiscard={(key) => {
          setRows((current) => discardRow(current, key));
          flow.close();
        }}
        onFocusCell={(key, field) => {
          flow.close();
          // The cell must be visible: clear the filters that could hide its row.
          setFilters({ search: '', building: '', pendingOnly: false });
          setFocus((f) => ({ rowKey: key, columnKey: field, nonce: (f?.nonce ?? 0) + 1 }));
        }}
      />
    </section>
  );
}

/** "עדכון אחד ו-2 דירות חדשות", leaving out parts that are zero. */
function changes(updated: number, created: number): string {
  const parts = [
    updated > 0 ? count(updated, 'עדכון אחד', 'עדכונים') : null,
    created > 0 ? count(created, 'דירה חדשה אחת', 'דירות חדשות') : null,
  ].filter((part): part is string => part !== null);
  const [first, second] = parts;
  if (first === undefined) return 'ללא שינוי';
  return second === undefined ? first : and(first, second);
}
